import { useState } from "react";
import { Button } from "../components/Button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../components/Card";
import { Input } from "../components/Input";
import { Badge } from "../components/Badge";
import { Toggle } from "../components/Toggle";
import { Alert, AlertTitle, AlertDescription } from "../components/Alert";

function Showcase({ title, description, specs, children, code }) {
  return (
    <article className="kin-surface rounded-[24px] p-6 sm:p-8">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-2xl">
          <h3 className="text-sm font-semibold tracking-[0.22em] uppercase text-kin-muted">
            {title}
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-kin-body/90">{description}</p>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:max-w-sm">
          {specs.map((spec) => (
            <div
              key={spec.label}
              className="rounded-xl border border-kin-white/50 bg-kin-white/60 shadow-sm backdrop-blur-md px-3 py-2"
            >
              <p className="text-[10px] font-semibold tracking-[0.15em] uppercase text-kin-muted">
                {spec.label}
              </p>
              <p className="mt-1 text-xs font-semibold text-kin-body">{spec.value}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-7">{children}</div>

      <div className="mt-7 overflow-x-auto rounded-2xl kin-code-block p-5">
        <pre className="text-xs leading-relaxed text-kin-sage font-mono">{code}</pre>
      </div>
    </article>
  );
}

function ButtonShowcase() {
  return (
    <Showcase
      title="Buttons"
      description="Buttons have a clear hierarchy: green fills for primary intent, neutral glass for secondary actions, and lavender accents for AI-driven moments. Motion is fluid and responsive."
      specs={[
        { label: "Radius", value: "12px" },
        { label: "Weight", value: "600" },
        { label: "Hover / Active", value: "Opacity 90% / scale(0.98)" },
        { label: "Focus", value: "2px ring" },
      ]}
      code={`<Button variant="primary" size="large">Create Plan</Button>
<Button variant="secondary">Preview</Button>
<Button variant="accent">Generate Insight</Button>
<Button variant="ghost">Dismiss</Button>`}
    >
      <div className="kin-surface-strong rounded-[20px] p-5 sm:p-6">
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Button size="large">Create Plan</Button>
          <Button variant="secondary" size="large">
            Preview
          </Button>
          <Button variant="accent" size="large">
            Generate Insight
          </Button>
          <Button variant="ghost" size="large">
            Dismiss
          </Button>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button variant="secondary">
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
              />
            </svg>
            Upload Data
          </Button>
          <Button variant="accent" size="icon" aria-label="Save to favorites">
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17.593 3.322c1.1.128 2.08.78 2.626 1.751.547.97.595 2.14.13 3.152l-6.11 13.292a2.25 2.25 0 01-4.196-.055L3.78 8.258a3.379 3.379 0 01.129-3.166 3.377 3.377 0 012.64-1.77l10.594-.001z"
              />
            </svg>
          </Button>
          <Button variant="disabled" size="medium" disabled>
            Syncing...
          </Button>
        </div>
      </div>
    </Showcase>
  );
}

function CardShowcase() {
  return (
    <Showcase
      title="Cards"
      description="Cards use Liquid Glass layering. They refract the background beautifully while maintaining strict concentricity and sharp inner glows."
      specs={[
        { label: "Radius", value: "24px" },
        { label: "Border", value: "White / 50%" },
        { label: "Shadow", value: "Crisp layered" },
        { label: "Interaction", value: "Active: scale(0.98)" },
      ]}
      code={`<Card variant="interactive">
  <CardHeader>
    <CardTitle>AI Insight</CardTitle>
    <CardDescription>Correlated sleep and energy patterns.</CardDescription>
  </CardHeader>
  <CardContent>...</CardContent>
</Card>`}
    >
      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        <Card>
          <CardContent>
            <div className="h-32 rounded-2xl bg-kin-waves-soft flex items-center justify-center">
              <svg
                className="h-8 w-8 text-kin-olive"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 16.5L8.25 11.25a2.25 2.25 0 013.182 0L16.5 16.5m-1.5-1.5l1.5-1.5a2.25 2.25 0 013.182 0l1.318 1.318M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z"
                />
              </svg>
            </div>
          </CardContent>
          <CardHeader>
            <CardTitle>Nutrition Snapshot</CardTitle>
            <CardDescription>
              Protein and fiber intake stayed consistently above target this week.
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button variant="link">Open report</Button>
          </CardFooter>
        </Card>

        <Card variant="quiet">
          <CardHeader>
            <CardDescription className="text-[11px] font-semibold tracking-[0.18em] uppercase text-kin-muted">
              Resting Heart Rate
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-2">
              <span className="text-5xl font-serif leading-none text-kin-black">62</span>
              <span className="pb-1 text-sm text-kin-muted">bpm</span>
            </div>
            <div className="mt-4 h-14 flex items-end gap-1">
              {[34, 48, 44, 58, 52, 69, 64, 72, 66, 75, 70, 82].map((h, idx) => (
                <div
                  key={idx}
                  className="flex-1 rounded-sm bg-kin-lavender/40"
                  style={{ height: `${h}%` }}
                />
              ))}
            </div>
          </CardContent>
        </Card>

        <Card variant="interactive">
          <CardHeader>
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-kin-lavender/15">
              <svg
                className="h-5 w-5 text-kin-dark"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.7}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"
                />
              </svg>
            </div>
            <CardTitle>New Correlation Found</CardTitle>
            <CardDescription>
              Sleep consistency has a strong positive relationship with your energy rating.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-kin-lavender/20">
                <div className="h-full w-4/5 rounded-full bg-kin-lavender" />
              </div>
              <span className="text-xs font-semibold text-kin-dark">81%</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </Showcase>
  );
}

function InputShowcase() {
  const [activeField, setActiveField] = useState("");

  return (
    <Showcase
      title="Form Fields"
      description="Form components prioritize clarity and confidence: crisp borders, prominent focus rings, explicit success and error states, and consistent control height."
      specs={[
        { label: "Height", value: "44px / 48px" },
        { label: "Radius", value: "16px" },
        { label: "Focus", value: "4px ring" },
        { label: "Label", value: "11px uppercase" },
      ]}
      code={`<label htmlFor="email" className="field-label">Email</label>
<Input id="email" className="mt-2" type="email" variant="success" aria-describedby="email-help" />
<p id="email-help">Looks valid</p>

<label htmlFor="dob" className="field-label">Date of Birth</label>
<Input id="dob" className="mt-2" type="text" variant="error" aria-invalid="true" aria-describedby="dob-error" />
<p id="dob-error">Use DD/MM/YYYY</p>`}
    >
      <div className="kin-surface-strong rounded-[20px] p-5 sm:p-6">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div>
            <label
              htmlFor="full-name"
              className="text-[11px] font-semibold tracking-[0.18em] uppercase text-kin-muted"
            >
              Full Name
            </label>
            <Input
              id="full-name"
              className="mt-2"
              type="text"
              placeholder="Enter your full name"
              aria-describedby={activeField === "name" ? "full-name-help" : undefined}
              onFocus={() => setActiveField("name")}
              onBlur={() => setActiveField("")}
            />
            {activeField === "name" && (
              <p
                id="full-name-help"
                className="mt-1.5 text-xs text-kin-green-dark"
                aria-live="polite"
              >
                Focused state active
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="email-address"
              className="text-[11px] font-semibold tracking-[0.18em] uppercase text-kin-muted"
            >
              Email Address
            </label>
            <Input
              id="email-address"
              className="mt-2"
              type="email"
              defaultValue="hello@kin.health"
              variant="success"
              autoComplete="email"
              aria-describedby="email-address-help"
            />
            <p id="email-address-help" className="mt-1.5 text-xs text-kin-green-dark">
              Looks valid
            </p>
          </div>

          <div>
            <label
              htmlFor="search-records"
              className="text-[11px] font-semibold tracking-[0.18em] uppercase text-kin-muted"
            >
              Search
            </label>
            <div className="relative mt-2">
              <svg
                className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-kin-muted"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                />
              </svg>
              <Input
                id="search-records"
                type="search"
                className="pl-11"
                placeholder="Search records"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="date-of-birth"
              className="text-[11px] font-semibold tracking-[0.18em] uppercase text-kin-muted"
            >
              Date of Birth
            </label>
            <Input
              id="date-of-birth"
              className="mt-2"
              type="text"
              defaultValue="13/25/1990"
              variant="error"
              aria-invalid="true"
              aria-describedby="date-of-birth-error"
            />
            <p id="date-of-birth-error" className="mt-1.5 text-xs text-kin-dark">
              Use DD/MM/YYYY
            </p>
          </div>

          <div className="md:col-span-2">
            <label
              htmlFor="clinician-notes"
              className="text-[11px] font-semibold tracking-[0.18em] uppercase text-kin-muted"
            >
              Notes
            </label>
            <textarea
              id="clinician-notes"
              rows={3}
              placeholder="Add context for your clinician"
              className="mt-2 w-full resize-none rounded-[16px] border border-kin-olive/16 bg-kin-white/85 px-4 py-3 text-sm text-kin-black placeholder:text-kin-muted/55 shadow-[inset_0_1px_0_rgba(255,255,255,0.9)] transition focus:border-kin-green/45 focus:outline-none focus:ring-4 focus:ring-kin-green/15"
            />
          </div>
        </div>
      </div>
    </Showcase>
  );
}

function BadgeAndToggleShowcase() {
  const [toggles, setToggles] = useState({
    notifications: true,
    weeklyDigest: false,
    dataSharing: true,
  });

  const toggleRows = [
    { key: "notifications", label: "Push notifications" },
    { key: "weeklyDigest", label: "Weekly digest" },
    { key: "dataSharing", label: "Research data sharing" },
  ];

  return (
    <Showcase
      title="Badges & Toggles"
      description="Status chips and binary controls use strong color semantics and compact geometry. They are intentionally high contrast so dashboard metadata can be scanned at a glance."
      specs={[
        { label: "Badge Height", value: "26px" },
        { label: "Toggle Width", value: "48px" },
        { label: "States", value: "Color + shape" },
        { label: "Use", value: "Metadata + settings" },
      ]}
      code={`<Badge variant="gold">Review</Badge>
<Badge variant="solidGreen">Active</Badge>

<Toggle checked={enabled} onClick={() => setEnabled(!enabled)} />`}
    >
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="kin-surface-strong rounded-[20px] p-5 sm:p-6">
          <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-kin-muted">
            Status
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Badge>
              <span className="h-1.5 w-1.5 rounded-full bg-kin-green" />
              Normal
            </Badge>
            <Badge variant="gold">
              <span className="h-1.5 w-1.5 rounded-full bg-kin-gold" />
              Review
            </Badge>
            <Badge variant="orange">
              <span className="h-1.5 w-1.5 rounded-full bg-kin-orange" />
              Attention
            </Badge>
            <Badge variant="lavender">
              <span className="h-1.5 w-1.5 rounded-full bg-kin-lavender" />
              Processing
            </Badge>
          </div>

          <p className="mt-6 text-[11px] font-semibold tracking-[0.18em] uppercase text-kin-muted">
            Category
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {["Blood Work", "Sleep", "Recovery", "Nutrition"].map((item) => (
              <Badge
                key={item}
                className="normal-case tracking-normal border-kin-olive/18 bg-kin-white/80 text-xs text-kin-body"
              >
                {item}
              </Badge>
            ))}
          </div>

          <p className="mt-6 text-[11px] font-semibold tracking-[0.18em] uppercase text-kin-muted">
            Solid
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Badge variant="solidGreen">Active</Badge>
            <Badge variant="solidGold">Pending</Badge>
            <Badge variant="solidOrange">Urgent</Badge>
            <Badge variant="solidOlive">Archived</Badge>
          </div>
        </div>

        <div className="kin-surface-strong rounded-[20px] p-5 sm:p-6">
          <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-kin-muted">
            Preferences
          </p>
          <div className="mt-4 space-y-4">
            {toggleRows.map((row) => (
              <div
                key={row.key}
                className="flex items-center justify-between rounded-xl border border-kin-white/50 bg-kin-white/70 px-3 py-3 shadow-sm backdrop-blur-md"
              >
                <span className="text-sm text-kin-body">{row.label}</span>
                <Toggle
                  checked={toggles[row.key]}
                  aria-label={row.label}
                  onClick={() => setToggles((prev) => ({ ...prev, [row.key]: !prev[row.key] }))}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </Showcase>
  );
}

function AlertShowcase() {
  const alertRows = [
    {
      variant: "success",
      title: "Records synced successfully",
      description: "New blood work data has been added to your timeline.",
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      ),
      iconClass: "text-kin-green",
    },
    {
      variant: "info",
      title: "Insight model updated",
      description: "Sleep recommendations now incorporate resting heart-rate drift.",
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
        />
      ),
      iconClass: "text-kin-dark",
    },
    {
      variant: "warning",
      title: "Vitamin D is below optimal range",
      description: "Current level is 18 ng/mL. Recommended range is 30-60 ng/mL.",
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
        />
      ),
      iconClass: "text-kin-gold-dark",
    },
    {
      variant: "error",
      title: "Upload failed",
      description: "The file type is unsupported. Try PDF, JPG, or PNG.",
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
        />
      ),
      iconClass: "text-kin-rose-dark",
    },
  ];

  return (
    <Showcase
      title="Alerts"
      description="Alert containers combine color tint, border coding, and iconography so severity is obvious without reading the full message. Tone stays calm and informative."
      specs={[
        { label: "Radius", value: "16px" },
        { label: "Spacing", value: "20 / 16" },
        { label: "Severity", value: "4 tiers" },
        { label: "Voice", value: "Actionable" },
      ]}
      code={`<Alert variant="warning">
  <AlertTitle>Vitamin D below optimal range</AlertTitle>
  <AlertDescription>Your level is 18 ng/mL.</AlertDescription>
</Alert>`}
    >
      <div className="space-y-3">
        {alertRows.map((row) => (
          <Alert key={row.title} variant={row.variant}>
            <div className="flex gap-3">
              <svg
                className={`mt-0.5 h-5 w-5 shrink-0 ${row.iconClass}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                {row.icon}
              </svg>
              <div>
                <AlertTitle>{row.title}</AlertTitle>
                <AlertDescription>{row.description}</AlertDescription>
              </div>
            </div>
          </Alert>
        ))}
      </div>
    </Showcase>
  );
}

function SpacingAndLayout() {
  const spacing = [4, 8, 12, 16, 24, 32, 48, 64];

  return (
    <Showcase
      title="Spacing & Rhythm"
      description="A disciplined spacing scale keeps the system refined at any density. Major components sit on a 4px base grid with calibrated jumps for macro layout."
      specs={[
        { label: "Base Grid", value: "4px" },
        { label: "Core Radius", value: "12-24px" },
        { label: "Section Gap", value: "64-80px" },
        { label: "Density", value: "Comfortable" },
      ]}
      code={`// spacing tokens
[4, 8, 12, 16, 24, 32, 48, 64]

// recommended radii
[12, 16, 20, 24]`}
    >
      <div className="kin-surface-strong rounded-[20px] p-5 sm:p-6">
        <div className="grid gap-8 lg:grid-cols-2">
          <div>
            <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-kin-muted">
              Scale
            </p>
            <div className="mt-4 space-y-3">
              {spacing.map((value) => (
                <div key={value} className="flex items-center gap-3">
                  <span className="w-10 text-right text-xs font-mono text-kin-muted">{value}</span>
                  <div
                    className="h-2 rounded-full bg-kin-gold/80"
                    style={{ width: `${Math.max(value * 2, 16)}px` }}
                  />
                  <span className="text-xs text-kin-body">{value}px</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-kin-muted">
              Radius
            </p>
            <div className="mt-4 flex items-end gap-5">
              <div className="flex flex-col items-center gap-2">
                <div className="h-12 w-12 rounded-xl border border-kin-olive/20 bg-kin-white/85" />
                <span className="text-xs font-mono text-kin-muted">12</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="h-14 w-14 rounded-2xl border border-kin-olive/20 bg-kin-white/85" />
                <span className="text-xs font-mono text-kin-muted">16</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="h-20 w-20 rounded-[24px] border border-kin-olive/20 bg-kin-white/85" />
                <span className="text-xs font-mono text-kin-muted">24</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="h-14 w-14 rounded-full border border-kin-olive/20 bg-kin-white/85" />
                <span className="text-xs font-mono text-kin-muted">full</span>
              </div>
            </div>

            <p className="mt-6 text-[11px] font-semibold tracking-[0.18em] uppercase text-kin-muted">
              Elevation
            </p>
            <div className="mt-3 flex items-end gap-4">
              <div className="h-14 w-20 rounded-xl border border-kin-white/60 bg-kin-white/80 shadow-sm" />
              <div className="h-14 w-20 rounded-xl border border-kin-white/60 bg-kin-white/80 shadow-[0_4px_16px_-4px_rgba(0,0,0,0.06)]" />
              <div className="h-14 w-20 rounded-xl border border-kin-white/60 bg-kin-white/80 shadow-[0_12px_32px_-8px_rgba(0,0,0,0.08)]" />
            </div>
          </div>
        </div>
      </div>
    </Showcase>
  );
}

export default function Components() {
  return (
    <section
      id="components"
      className="kin-section-glow relative overflow-hidden border-t border-kin-olive/10 px-6 py-14 scroll-mt-16 gradient-wash sm:px-8 sm:py-16"
    >
      <div className="pointer-events-none absolute left-1/2 top-0 h-80 w-80 -translate-x-1/2 rounded-full bg-kin-gold/[0.08] blur-3xl" />
      <div className="pointer-events-none absolute -right-20 top-60 h-80 w-80 rounded-full bg-kin-lavender/[0.08] blur-3xl" />

      <div className="relative mx-auto max-w-6xl">
        <p className="text-sm font-medium tracking-widest uppercase text-kin-muted">
          06 - Components
        </p>

        <div className="mt-6 max-w-3xl">
          <h2 className="font-serif text-4xl font-bold text-kin-black sm:text-5xl">
            Premium interaction system
          </h2>
          <p className="mt-3 text-base leading-relaxed text-kin-body">
            This component library translates Kin&apos;s brand voice into production-ready UI. Every
            primitive now shares the same fluid liquid glass depth model, motion language, and
            accessibility behavior so products feel cohesive from first click to advanced workflows.
          </p>
        </div>

        <div className="mt-12 space-y-8">
          <ButtonShowcase />
          <CardShowcase />
          <InputShowcase />
          <BadgeAndToggleShowcase />
          <AlertShowcase />
          <SpacingAndLayout />
        </div>
      </div>
    </section>
  );
}
